import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getAllBookings, updateBookingStatus, deleteBooking } from '../firebase/availability';
import { useToast } from '../components/Toast';
import { useOffline } from './useOffline';
import { debounce } from '../utils/debounce';
import { isToday } from '../utils/dateHelpers';
import { retryWithBackoff } from '../utils/errorHandler';

/**
 * Custom hook for managing bookings data.
 * Handles loading, filtering, searching, status updates, deletion, and export.
 */
export function useBookings({ isAuthenticated, activeTab }) {
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'confirmed', 'cancelled'

    const { showToast } = useToast();
    const { isOnline } = useOffline();
    const autoRefreshRef = useRef(null);

    // Load bookings from Firestore
    const loadBookings = useCallback(async () => {
        if (!isOnline) {
            showToast('You are offline. Cannot load bookings.', 'error');
            return;
        }

        setLoading(true);
        try {
            const allBookings = await retryWithBackoff(
                () => getAllBookings(),
                3,
                1000
            );
            setBookings(allBookings || []);
        } catch (error) {
            console.error('Error loading bookings:', error);
            showToast('Error loading bookings. Please refresh.', 'error');
            setBookings([]);
        } finally {
            setLoading(false);
        }
    }, [isOnline, showToast]);

    // Auto-refresh bookings when on bookings tab
    useEffect(() => {
        if (isAuthenticated && activeTab === 'bookings') {
            loadBookings();
            // Auto-refresh every 30 seconds
            autoRefreshRef.current = setInterval(() => {
                loadBookings();
            }, 30000);
        } else {
            // Clear interval when not on bookings tab
            if (autoRefreshRef.current) {
                clearInterval(autoRefreshRef.current);
                autoRefreshRef.current = null;
            }
        }

        return () => {
            if (autoRefreshRef.current) {
                clearInterval(autoRefreshRef.current);
                autoRefreshRef.current = null;
            }
        };
    }, [isAuthenticated, activeTab, loadBookings]);

    // Debounced search with null checks
    const debouncedSearch = useCallback(
        debounce((term, bookingsList, status) => {
            if (!Array.isArray(bookingsList)) {
                setFilteredBookings([]);
                return;
            }

            let filtered = [...bookingsList];

            if (term && typeof term === 'string' && term.trim()) {
                const searchLower = term.toLowerCase().trim();
                filtered = filtered.filter(booking => {
                    if (!booking || typeof booking !== 'object') return false;
                    return (
                        (booking.name && typeof booking.name === 'string' && booking.name.toLowerCase().includes(searchLower)) ||
                        (booking.email && typeof booking.email === 'string' && booking.email.toLowerCase().includes(searchLower)) ||
                        (booking.date && typeof booking.date === 'string' && booking.date.includes(searchLower)) ||
                        (booking.time && typeof booking.time === 'string' && booking.time.toLowerCase().includes(searchLower)) ||
                        (booking.notes && typeof booking.notes === 'string' && booking.notes.toLowerCase().includes(searchLower))
                    );
                });
            }

            if (status && status !== 'all' && typeof status === 'string') {
                filtered = filtered.filter(booking => booking && booking.status === status);
            }

            setFilteredBookings(filtered);
        }, 300),
        []
    );

    // Update filtered bookings when search/filter changes
    useEffect(() => {
        debouncedSearch(searchTerm, bookings, statusFilter);
    }, [bookings, searchTerm, statusFilter, debouncedSearch]);

    // Statistics with null checks
    const stats = useMemo(() => {
        if (!Array.isArray(bookings)) {
            return { total: 0, pending: 0, confirmed: 0, cancelled: 0, today: 0 };
        }

        const total = bookings.length;
        const pending = bookings.filter(b => b && b.status === 'pending').length;
        const confirmed = bookings.filter(b => b && b.status === 'confirmed').length;
        const cancelled = bookings.filter(b => b && b.status === 'cancelled').length;
        const today = bookings.filter(b => {
            if (!b || !b.date || typeof b.date !== 'string') return false;
            try {
                const bookingDate = new Date(b.date + 'T00:00:00');
                return isToday(bookingDate);
            } catch {
                return false;
            }
        }).length;

        return { total, pending, confirmed, cancelled, today };
    }, [bookings]);

    // Status change handler
    const handleStatusChange = useCallback(async (bookingId, newStatus) => {
        try {
            const result = await updateBookingStatus(bookingId, newStatus);
            if (result.success) {
                showToast(`Booking ${newStatus} successfully`, 'success');
                loadBookings();
            } else {
                showToast('Error updating booking: ' + result.error, 'error');
            }
        } catch (error) {
            showToast('Error updating booking: ' + error.message, 'error');
        }
    }, [showToast, loadBookings]);

    // Delete handler
    const handleDeleteBooking = useCallback(async (bookingId) => {
        if (!confirm('Are you sure you want to delete this booking? This action cannot be undone.')) return;

        try {
            const result = await deleteBooking(bookingId);
            if (result.success) {
                showToast('Booking deleted successfully', 'success');
                loadBookings();
            } else {
                showToast('Error deleting booking: ' + result.error, 'error');
            }
        } catch (error) {
            showToast('Error deleting booking: ' + error.message, 'error');
        }
    }, [showToast, loadBookings]);

    // CSV export
    const exportBookings = useCallback(() => {
        const escapeCSV = (str) => {
            if (!str) return '';
            const strValue = String(str);
            const doubleQuote = '"';
            return strValue.split(doubleQuote).join(doubleQuote + doubleQuote);
        };

        const csv = [
            ['Date', 'Time', 'Name', 'Email', 'Status', 'Notes', 'Created'].join(','),
            ...filteredBookings.map(booking => [
                booking.date,
                booking.time,
                `"${escapeCSV(booking.name)}"`,
                booking.email,
                booking.status,
                `"${escapeCSV(booking.notes)}"`,
                booking.createdAt?.toDate?.()?.toISOString() || ''
            ].join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const dateStr = new Date().toISOString().split('T')[0];
        a.download = `bookings-${dateStr}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('Bookings exported successfully', 'success');
    }, [filteredBookings, showToast]);

    // Status color helper
    const getStatusColor = useCallback((status) => {
        switch (status) {
            case 'confirmed': return 'bg-green-100 text-green-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-yellow-100 text-yellow-800';
        }
    }, []);

    // Clear filters
    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setStatusFilter('all');
    }, []);

    return {
        // Data
        bookings,
        filteredBookings,
        loading,
        stats,

        // Search/filter state
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,

        // Actions
        loadBookings,
        handleStatusChange,
        handleDeleteBooking,
        exportBookings,
        clearFilters,

        // Helpers
        getStatusColor
    };
}

export default useBookings;
